<template>
  <div class="teams-schema-edit-view container py-4">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2>Mitgliederfelder bearbeiten</h2>
      <BackButton fallback="teams-settings" :params="{ id: teamId }" />
    </div>

    <!-- Breadcrumbs -->
    <nav aria-label="breadcrumb" class="mb-4">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link :to="{ name: 'teams-list' }" class="text-decoration-none">
            Bereiche
          </router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link
            :to="{ name: 'teams-detail', params: { id: teamId } }"
            class="text-decoration-none"
          >
            {{ selectedTeam?.name || 'Bereich' }}
          </router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link
            :to="{ name: 'teams-settings', params: { id: teamId } }"
            class="text-decoration-none"
          >
            Bereich-Einstellungen
          </router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link
            :to="{ name: 'teams-edit', params: { id: teamId } }"
            class="text-decoration-none"
          >
            Bearbeiten
          </router-link>
        </li>
        <li class="breadcrumb-item active" aria-current="page">Mitgliederfelder bearbeiten</li>
      </ol>
    </nav>

    <!-- Team Mitgliederfelder Edit Component -->
    <TeamSchemaEdit
      v-if="selectedTeam && !loading"
      :team="selectedTeam"
      @back="handleBack"
      @success="handleSchemaEditSuccess"
      @error="handleEditError"
    />

    <!-- Loading State -->
    <div v-else-if="loading" class="text-center py-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Laden...</span>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      {{ error }}
    </div>

    <!-- No Permission -->
    <div v-else-if="!isAdminOrCreator" class="alert alert-danger" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      Du hast keine Berechtigung, dieses Team zu bearbeiten. Nur Team-Administratoren und
      Team-Ersteller können Teams bearbeiten.
      <div class="mt-2">
        <BackButton fallback="teams-detail" :params="{ id: teamId }" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useTeamsStore } from '@/stores/teams'
import { useAuthStore } from '@/stores/auth'
import { showSuccess, showError } from '@/services/notification'
import TeamSchemaEdit from '@/components/teams/TeamSchemaEdit.vue'
import BackButton from '@/components/common/BackButton.vue'

const router = useRouter()
const route = useRoute()
const teamsStore = useTeamsStore()
const authStore = useAuthStore()

const { selectedTeam, loading, error } = storeToRefs(teamsStore)
const currentUser = computed(() => authStore.user)

const teamId = computed(() => route.params.id)

const isAdminOrCreator = computed(() => {
  if (!selectedTeam.value || !currentUser.value) return false
  return (
    selectedTeam.value.role === 'teamAdmin' || selectedTeam.value.creatorId === currentUser.value.id
  )
})

const handleBack = () => {
  router.push({ name: 'teams-settings', params: { id: teamId.value } })
}

const handleSchemaEditSuccess = async (updatedTeam) => {
  await teamsStore.loadTeamById(teamId.value)
  await teamsStore.refreshTeams()
  showSuccess('Bereich-Mitgliederfelder wurde erfolgreich aktualisiert')
  router.push({ name: 'teams-settings', params: { id: teamId.value } })
}

const handleEditError = (errorMessage) => {
  showError(errorMessage || 'Fehler beim Aktualisieren des Bereich-Schemas')
}

onMounted(async () => {
  if (!selectedTeam.value || selectedTeam.value.id !== teamId.value) {
    await teamsStore.loadTeamById(teamId.value)
  }
})
</script>

<style scoped>
.teams-schema-edit-view {
  min-height: 100vh;
}
</style>
